import styled from '@emotion/styled';
import { ROOT_NODE } from '@kizen/page-builder';
import { areTreesEqual } from '@kizen/page-builder/utils/are-equal';
import { getDefaultHomepageTemplate } from '@kizen/page-builder/utils/build';
import { getStaticBlockWidthFromColumns } from '@kizen/page-builder/nodes/utils';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { breakpoints, isMobile, useWindowSize } from '__app/spacing';
import { KizenTypography } from '__app/typography';
import Switch from '__components/Kizen/Switch';
import FullscreenModal from 'components/Modals/Fullscreen';
import {
  ConfirmExitModal,
  PageBuilderCanvas,
  PageBuilderCanvasHeaderContainer,
  PageBuilderContainer,
  PageBuilderTray,
  PageBuilderHeader,
} from '__components/PageBuilder';

import {
  homePageCreatorElements,
  homepageNodes,
  homepageTrayItems,
  homePageNodeSettings,
} from 'components/PageBuilder/presets';
import { PageSettingsSection } from 'components/PageBuilder/settings/PageSettings';
import { TaskType } from 'components/WYSIWYG';
import { useModalControl } from 'hooks/useModalControl';
import { maybeTrackAIRequest } from 'utility/analytics';
import { ColumnSize, ColumnSizing } from './ColumnSizing';
import { useHomepageMergeFields } from './useHomepageMergeFields';
import { DashboardStyleSetting } from 'ts-components/StyleSettings/types';
type HomepageBuilderProps = {
  modalLayer: number;
  onCancel(): void;
  onSave(data: any): void;
  onSaveAndClose(data: any): void;
  existing?: any;
  defaultColumnSize?: ColumnSize;
  allowRelativeLinks?: boolean;
  defaultSettings?: DashboardStyleSetting;
};

type HeaderControlsProps = {
  columnSize: ColumnSize;
  dropshadow: boolean;
  onColumnSizeChange(size: ColumnSize): void;
  onDropshadowChange(dropshadow: boolean): void;
};

const HEADER_HEIGHT = 60;

const HeaderControlsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  height: inherit;
`;

const MobileContainer = styled.div`
  margin: 20px;
  text-align: center;
`;

const HeaderControls = ({
  columnSize,
  dropshadow,
  onColumnSizeChange,
  onDropshadowChange,
}: HeaderControlsProps) => {
  const { t } = useTranslation();

  return (
    <HeaderControlsContainer>
      <Switch
        checked={dropshadow}
        label={t('Dropshadow')}
        textPlacement="left"
        removeMargin={true}
        onChange={(ev) => onDropshadowChange(ev.target.checked)}
      />
      <ColumnSizing value={columnSize} onChange={onColumnSizeChange} />
    </HeaderControlsContainer>
  );
};

const HomepageBlockSettings = () => {
  const { t } = useTranslation();
  return <PageSettingsSection title={t('Block Settings')} />;
};

export const HomepageBuilder = ({
  modalLayer,
  onCancel,
  onSave,
  onSaveAndClose,
  existing,
  defaultColumnSize = 6,
  defaultSettings,
  allowRelativeLinks = false,
}: HomepageBuilderProps) => {
  const { t } = useTranslation();
  const [init] = useState(getDefaultHomepageTemplate(t));
  const canvasRef = useRef<any>();
  const editsRef = useRef<any>(existing ?? init);
  const [isConfirmModalOpen, { showModal, hideModal }] = useModalControl();
  const [columnSize, setColumnSize] = useState<ColumnSize>(defaultColumnSize);
  const [dropshadow, setDropshadow] = useState(
    existing?.ROOT?.props?.hasShadow ?? defaultSettings?.dropShadow ?? true
  );
  const { width } = useWindowSize() ?? { width: 0 };
  const mobileDisplay = isMobile(width, breakpoints.lg);
  const mergeFields = useHomepageMergeFields();
  const businessId = useSelector(
    (s: any) => s.authentication.chosenBusiness.id
  );

  const handleDropshadowChange = useCallback((value: boolean) => {
    setDropshadow(value);
    canvasRef.current.editor.actions.setProp(ROOT_NODE, (p: any) => {
      p.hasShadow = value;
    });
  }, []);

  const handleSave = () => {
    const current = canvasRef.current.editor.query.getSerializedNodes();
    onSave(current);
    editsRef.current = current;
  };

  const handleSaveAndClose = () => {
    onSaveAndClose(canvasRef.current.editor.query.getSerializedNodes());
  };

  const handleGoBack = () => {
    const current = canvasRef.current.editor.query.getSerializedNodes();
    if (!areTreesEqual(current, editsRef.current)) {
      showModal();
    } else {
      onCancel();
    }
  };

  const onAIRequestStart = useCallback(
    (requestType: TaskType) => {
      maybeTrackAIRequest('homepage', '', businessId, requestType);
    },
    [businessId]
  );

  return (
    <>
      {isConfirmModalOpen && (
        <ConfirmExitModal
          heading={t('Your Content Has Unsaved Changes')}
          onDiscard={() => onCancel()}
          onCancel={hideModal}
          onSave={handleSaveAndClose}
        >
          {t('All unsaved changes will be lost unless you save your content.')}
        </ConfirmExitModal>
      )}
      <FullscreenModal
        show
        enforceFocus={false}
        header={
          <PageBuilderHeader
            onGoBack={handleGoBack}
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
          />
        }
      >
        {mobileDisplay ? (
          <MobileContainer>
            <KizenTypography>
              {t(
                'This page has not been optimized for mobile. Please revisit on a desktop to receive the best experience.'
              )}
            </KizenTypography>
          </MobileContainer>
        ) : (
          <PageBuilderContainer
            enableAI
            enableGoogleFonts
            enableTextLinks
            enableMergeFields
            nodes={homepageNodes}
            creatorElements={homePageCreatorElements}
            measureRootWidth={false}
            initialGridlinesActive={true}
            staticBlock={true}
            modalLayer={modalLayer}
            allowRelativeLinks={allowRelativeLinks}
            mergeFields={mergeFields}
            onAIRequestStart={onAIRequestStart}
          >
            <PageBuilderCanvasHeaderContainer height={HEADER_HEIGHT}>
              <HeaderControls
                columnSize={columnSize}
                dropshadow={dropshadow}
                onColumnSizeChange={setColumnSize}
                onDropshadowChange={handleDropshadowChange}
              />
            </PageBuilderCanvasHeaderContainer>
            <PageBuilderCanvas
              ref={canvasRef}
              data={existing ?? init}
              headerHeight={HEADER_HEIGHT}
              columns={columnSize}
              widthDisplay={getStaticBlockWidthFromColumns(columnSize)}
              showBoxShadow={false}
              canResizeHeight={true}
              heightResizeStep={115}
            />
            <PageBuilderTray
              trayItems={homepageTrayItems}
              nodeSettings={homePageNodeSettings}
              RootNodeSettings={HomepageBlockSettings}
              canResize={false}
            />
          </PageBuilderContainer>
        )}
      </FullscreenModal>
    </>
  );
};
